import { Chart, ChartType, Plugin } from 'chart.js';
import { Subscription } from 'rxjs';
import { DeepLinkService } from './deep-link.service';

/**
 * Chart.js plugins and defaults shared by every chart of the dashboard (registered once in AppModule).
 *
 * deepLink — mirrors a chart's series selection and zoom window in the deep link (DeepLinkService):
 *   options.plugins.deepLink = { id: '<chart id>' }   (no id: the chart is not linked)
 *   Every dataset should carry a stable `linkKey` ('ist', 'soll', 'boilerTempC' …): labels are
 *   translated and indices shift with optional datasets; without one the index is used.
 *   - Legend clicks (Chart.js hides via the dataset's meta) are written to the link as hide=/show=
 *     (relative to the dataset's own `hidden` default), and re-applied to the fresh metas Chart.js
 *     creates whenever a component rebuilds its datasets — so the selection survives live updates.
 *   - The zoom window is written by {@link linkZoomComplete} (wired into dragZoom's onZoomComplete,
 *     which chartjs-plugin-zoom also calls on resetZoom) and re-applied (chart.zoomScale) after the
 *     chart is created or its options are replaced (ng2-charts assigns new options on every rebuild).
 *
 * yMinSpan — a minimum span for vertical linear axes, so quantisation noise (17.84 ↔ 17.85 °C) does
 *   not fill the chart height when a value hardly moves. The span is MIN_SPAN_STEPS × the axis'
 *   resolution: the scale option `resolution` = the precision of the axis' source / display (1 for
 *   the whole-degree Fröling values, 0.1 for the room sensors, 1 for % or W); without it the larger
 *   of a precision guessed from the magnitude and the data's own step (smallest change between
 *   consecutive values, ignored when it is large against the values, like an on/off series).
 *   Scale options: `resolution`, `minSpan` (absolute override; 0 = off).
 *   Applies to line charts, and to any chart whose axis sets `resolution` / `minSpan`.
 *   User min / max stay as set; data that is never negative is not pushed below zero.
 *
 * Defaults: monotone interpolation for smoothed lines (a curve that never over- or undershoots
 *   between two points — the "tension" bezier made flat values wobble), and line-style legend and
 *   tooltip swatches in the line's own colour (dash included) for line charts.
 */

// Typings for the options / dataset fields above (so chart configs need no casts).
declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    deepLink?: { id?: string | null };
  }
  interface CoreScaleOptions {
    /** yMinSpan: the precision the axis displays (e.g. 0.1 for °C with one decimal). */
    resolution?: number;
    /** yMinSpan: absolute minimum span of the axis; 0 switches the minimum span off. */
    minSpan?: number;
  }
  interface ChartDatasetProperties<TType extends ChartType, TData> {
    /** deepLink: stable, language-independent key of the dataset ('ist', 'soll', 'boilerTempC' …). */
    linkKey?: string;
  }
}

/** A quantisation step may take at most 1/MIN_SPAN_STEPS of the axis height. */
export const MIN_SPAN_STEPS = 10;

/** Stable key of a dataset for the deep link. */
export function datasetLinkKey(ds: any, index: number): string {
  return typeof ds?.linkKey === 'string' && ds.linkKey ? ds.linkKey : String(index);
}

function linkId(chart: Chart): string | null {
  const id = (chart.config.options as any)?.plugins?.deepLink?.id;
  return typeof id === 'string' && id ? id : null;
}

/** The chart's time axis: the horizontal linear / time scale (a category axis has no time window). */
function xScale(chart: Chart): any {
  return Object.values(chart.scales).find((s: any) => s.isHorizontal() && s.type !== 'category') ?? null;
}

interface LinkChartState { optionsRef: unknown; rebuilt: boolean; destroyed: boolean; }
const linkState = new WeakMap<Chart, LinkChartState>();
const liveCharts = new Set<Chart>();
let externalSub: Subscription | null = null;

/** The link changed from outside (back / forward, an edited link): forget the charts' own state so
 *  the link's applies again (visibility on the next update, zoom right away). */
function reapplyAll(): void {
  for (const chart of liveCharts) {
    const id = linkId(chart);
    const svc = DeepLinkService.instance;
    if (!id || !svc) continue;
    chart.data.datasets.forEach((_, i) => { (chart.getDatasetMeta(i) as any).hidden = null; });
    const st = linkState.get(chart);
    if (st) st.rebuilt = true;
    if (!svc.zoom(id) && (chart as any).isZoomedOrPanned?.()) (chart as any).resetZoom?.('none');
    chart.update('none');
  }
}

/**
 * The chart's zoom changed (end of a drag / pinch, or a reset): store or drop its window in the link.
 * Call it from a chart's zoom.onZoomComplete if it does not use chart.utils' dragZoom().
 */
export function linkZoomComplete(chart: Chart): void {
  const id = linkId(chart);
  const svc = DeepLinkService.instance;
  if (!id || !svc) return;
  const x = xScale(chart);
  if (!x) return;
  if ((chart as any).isZoomedOrPanned?.() && isFinite(x.min) && isFinite(x.max)) {
    svc.setZoom(id, { from: x.min, to: x.max });
  } else {
    svc.setZoom(id, null);
  }
}

/** The chart's link bookkeeping, created once it has a link id (also when the id comes later). */
function stateOf(chart: Chart): LinkChartState | null {
  let st = linkState.get(chart);
  if (!st && linkId(chart)) {
    st = { optionsRef: undefined, rebuilt: true, destroyed: false };
    linkState.set(chart, st);
    liveCharts.add(chart);
    if (!externalSub && DeepLinkService.instance) {
      externalSub = DeepLinkService.instance.external$.subscribe(() => reapplyAll());
    }
  }
  return st ?? null;
}

export const deepLinkPlugin: Plugin = {
  id: 'deepLink',

  beforeUpdate(chart) {
    const st = stateOf(chart);
    if (!st) return;
    // A rebuild replaces the options (ng2-charts assigns a freshly merged options object, its
    // `plugins` included), which drops the zoom. Chart.js' own updates (a zoom / pinch step, a
    // legend click) keep `plugins` but re-create `scales` every time, so `scales` cannot tell.
    const ref = (chart.config.options as any)?.plugins;
    if (ref !== st.optionsRef) { st.optionsRef = ref; st.rebuilt = true; }
  },

  // After Chart.js has (re)built the dataset metas, before anything is laid out or drawn.
  beforeElementsUpdate(chart) {
    const id = linkId(chart);
    const svc = DeepLinkService.instance;
    if (!id || !svc) return;
    const stored = svc.visibility(id);
    const next = new Map(stored);
    let changed = false;
    chart.data.datasets.forEach((ds: any, i) => {
      const key = datasetLinkKey(ds, i);
      const meta: any = chart.getDatasetMeta(i);
      const byDefault = !!ds.hidden;
      if (typeof meta.hidden === 'boolean') {
        // set by a legend click (or applied by us earlier): that is the current selection
        if (meta.hidden === byDefault) { if (next.delete(key)) changed = true; }
        else if (next.get(key) !== meta.hidden) { next.set(key, meta.hidden); changed = true; }
      } else if (stored.has(key)) {
        // a fresh meta (new chart / rebuilt datasets): the link decides
        meta.hidden = stored.get(key)!;
      }
      // Chart.js set meta.visible in buildOrUpdateControllers, before this hook: scales, drawing and
      // the legend read it, so it must follow the hidden flag set just now
      meta.visible = chart.isDatasetVisible(i);
    });
    if (changed) svc.setVisibility(id, next);
  },

  afterUpdate(chart) {
    const st = linkState.get(chart);
    if (!st || !st.rebuilt) return;
    st.rebuilt = false;
    const id = linkId(chart);
    const svc = DeepLinkService.instance;
    if (!id || !svc || !svc.zoom(id)) return;
    // Not inside Chart.update(): zoomScale updates the chart itself.
    queueMicrotask(() => {
      const win = svc.zoom(id);
      const x = xScale(chart);
      if (st.destroyed || !win || !x || !(chart as any).zoomScale) return;
      if (Math.abs(x.min - win.from) < 1000 && Math.abs(x.max - win.to) < 1000) return;
      (chart as any).zoomScale(x.id, { min: win.from, max: win.to }, 'none');
      // let the component know it is zoomed (its Reset button), like a drag zoom does
      const onComplete = (chart.config.options as any)?.plugins?.zoom?.zoom?.onZoomComplete;
      if (typeof onComplete === 'function') onComplete({ chart });
    });
  },

  afterDestroy(chart) {
    const st = linkState.get(chart);
    if (st) st.destroyed = true;
    liveCharts.delete(chart);
  },
};

/** Smallest change between consecutive finite values (0 if the values never change). */
function dataStep(values: number[]): number {
  let step = Infinity;
  let prev: number | null = null;
  for (const v of values) {
    if (typeof v !== 'number' || !isFinite(v)) continue;
    if (prev != null) {
      const d = Math.abs(Math.round((v - prev) * 1e6) / 1e6);
      if (d > 0 && d < step) step = d;
    }
    prev = v;
  }
  return isFinite(step) ? step : 0;
}

export const yMinSpanPlugin: Plugin = {
  id: 'yMinSpan',

  afterDataLimits(chart, args: any) {
    const scale = args?.scale;
    if (!scale || scale.isHorizontal() || scale.type !== 'linear') return;
    const opts = scale.options ?? {};
    const explicit = typeof opts.minSpan === 'number' || typeof opts.resolution === 'number';
    if (!explicit && (chart.config as any).type !== 'line') return;
    if (opts.minSpan === 0) return;
    let { min, max } = scale;
    if (!isFinite(min) || !isFinite(max)) return;
    const { minDefined, maxDefined } = scale.getUserBounds();
    if (minDefined && maxDefined) return;

    // the values drawn against this axis
    const values: number[] = [];
    let allNonNegative = true;
    for (const meta of chart.getSortedVisibleDatasetMetas()) {
      if ((meta as any).vScale !== scale && (meta as any).yScale !== scale) continue;
      for (const p of (meta as any)._parsed ?? []) {
        const v = p?.[scale.axis];
        if (typeof v === 'number' && isFinite(v)) { values.push(v); if (v < 0) allNonNegative = false; }
      }
    }
    if (!values.length) return;

    let minSpan: number;
    if (typeof opts.minSpan === 'number') {
      minSpan = opts.minSpan;
    } else if (typeof opts.resolution === 'number') {
      // the chart knows its source's precision: archive bucket means (17.8433) or on/off values
      // (0 ↔ 100 %) would mislead a detection
      minSpan = MIN_SPAN_STEPS * opts.resolution;
    } else {
      const absMax = Math.max(Math.abs(min), Math.abs(max));
      // unknown display precision: two significant digits below the magnitude (17.8 → 0.1, 1500 → 10)
      const shown = absMax > 0 ? Math.pow(10, Math.floor(Math.log10(absMax)) - 2) : 0.01;
      // a quantisation step is small against the values (1 °C at 35 °C); a jump between two levels
      // (an on/off series) is not one
      const step = dataStep(values);
      minSpan = MIN_SPAN_STEPS * Math.max(shown, step <= 0.05 * absMax ? step : 0);
    }
    if (!(minSpan > 0) || max - min >= minSpan) return;

    const centre = (min + max) / 2;
    let lo = centre - minSpan / 2;
    let hi = centre + minSpan / 2;
    if (minDefined) { lo = min; hi = min + minSpan; }
    else if (maxDefined) { hi = max; lo = max - minSpan; }
    else if (allNonNegative && lo < 0) { lo = 0; hi = minSpan; }   // the data, not Chart.js' padded min
    scale.min = lo;
    scale.max = hi;
  },
};

/** Register the plugins and set the dashboard-wide chart defaults (call once, after the zoom plugin). */
export function registerChartDefaults(): void {
  Chart.register(deepLinkPlugin, yMinSpanPlugin);

  // No over- / undershoot between points: a flat 17.8 °C stays flat.
  (Chart.defaults.elements.line as any).cubicInterpolationMode = 'monotone';

  // Line charts: legend + tooltip swatches drawn as the line itself (its colour and dash), not as a
  // box filled with the (often transparent) background colour.
  const lineOverrides: any = (Chart.overrides as any).line;
  lineOverrides.plugins = lineOverrides.plugins ?? {};
  lineOverrides.plugins.legend = {
    ...(lineOverrides.plugins.legend ?? {}),
    labels: {
      usePointStyle: true,
      pointStyle: 'line',
      pointStyleWidth: 22,
      // Chart.js takes a point-style swatch's colours from the first point's style (pointBorderColor
      // etc.): use the line's own colour, width and dash instead
      generateLabels: (chart: Chart) => {
        const items = (Chart.defaults.plugins.legend.labels as any).generateLabels(chart) as any[];
        return items.map(item => {
          const ds: any = chart.data.datasets[item.datasetIndex];
          // a shaded area (the burner): a filled box, not a line — it would read as the line of the same hue
          if (ds?.legendBox) {
            return { ...item, fillStyle: ds.legendFill ?? ds.backgroundColor, strokeStyle: ds.borderColor, lineDash: [], lineWidth: 1, pointStyle: 'rect' };
          }
          const style: any = (chart.getDatasetMeta(item.datasetIndex) as any).controller?.getStyle(undefined) ?? {};
          const color = typeof style.borderColor === 'string' ? style.borderColor : item.strokeStyle;
          return {
            ...item,
            fillStyle: color,
            strokeStyle: color,
            lineDash: style.borderDash ?? [],
            lineWidth: Math.max(2, Math.min(3, Number(style.borderWidth) || 2)),
            pointStyle: 'line',
          };
        });
      },
    },
  };
  lineOverrides.plugins.tooltip = {
    ...(lineOverrides.plugins.tooltip ?? {}),
    callbacks: {
      labelColor: (item: any) => {
        const c = item.dataset?.borderColor;
        const color = typeof c === 'string' ? c : item.dataset?.backgroundColor;
        return { borderColor: color, backgroundColor: color };
      },
    },
  };
}
